import { useState, useEffect } from 'react';
import { CiBullhorn } from 'react-icons/ci';
import { LuQuote } from 'react-icons/lu';
import { LuList } from 'react-icons/lu';
import { LuHeading3 } from 'react-icons/lu';
import { LuHeading1, LuHeading2 } from 'react-icons/lu';
import { BlockAdapter } from '../context';
import { BlockItemController } from './blockItemInterface';
import { BlockItemControllerProps, BlockItemViewProps } from './blockItemProps';
import { BiCommentAdd, BiCommentEdit, BiText } from 'react-icons/bi';
import { HiOutlineStar, HiOutlineTrash } from 'react-icons/hi';
import { BlockItemAggregate } from './blockItemModel';
import { useDebouncedCallback } from 'use-debounce';

export function createBlockItemController(): BlockItemController {
    function getInsertMenu(
        item: BlockItemAggregate,
        props: BlockItemControllerProps
    ): BlockItemViewProps['insert'] {
        if (!props.adapter.isEmpty) {
            return null;
        }
        if (props.adapter.node.type !== 'text') {
            return null;
        }
        return {
            menu: {
                popover: {
                    isOpen: false,
                    onOpen: () => {},
                    onClose: () => {},
                },
                items: [
                    {
                        kind: 'item',
                        id: 'text',
                        label: 'Text',
                        Icon: BiText,
                        colorScheme: null,
                        button: {
                            onClick: () => {
                                props.adapter.insert({ kind: 'text', items: [] });
                            },
                        },
                    },
                    {
                        kind: 'item',
                        id: 'heading_1',
                        label: 'Heading 1',
                        Icon: LuHeading1,
                        colorScheme: null,
                        button: {
                            onClick: () => {
                                props.adapter.insert({
                                    kind: 'heading',
                                    level: 1,
                                    items: [],
                                });
                            },
                        },
                    },
                    {
                        kind: 'item',
                        id: 'heading_2',
                        label: 'Heading 2',
                        Icon: LuHeading2,
                        colorScheme: null,
                        button: {
                            onClick: () => {
                                props.adapter.insert({
                                    kind: 'heading',
                                    level: 2,
                                    items: [],
                                });
                            },
                        },
                    },
                    {
                        kind: 'item',
                        id: 'heading_3',
                        label: 'Heading 3',
                        Icon: LuHeading3,
                        colorScheme: null,
                        button: {
                            onClick: () => {
                                props.adapter.insert({
                                    kind: 'heading',
                                    level: 3,
                                    items: [],
                                });
                            },
                        },
                    },
                    {
                        kind: 'item',
                        id: 'bullet_list',
                        label: 'Bullet List',
                        Icon: LuList,
                        colorScheme: null,
                        button: {
                            onClick: () => {
                                props.adapter.insert({
                                    kind: 'list_item',
                                    items: [],
                                    style: 'bullet',
                                });
                            },
                        },
                    },
                    {
                        kind: 'item',
                        id: 'quote',
                        label: 'Quote',
                        Icon: LuQuote,
                        colorScheme: null,
                        button: {
                            onClick: () => {
                                props.adapter.insert({ kind: 'quote', items: [] });
                            },
                        },
                    },
                    {
                        kind: 'item',
                        id: 'callout',
                        label: 'Callout',
                        Icon: CiBullhorn,
                        colorScheme: null,
                        button: {
                            onClick: () => {
                                props.adapter.insert({ kind: 'callout', text: '' });
                            },
                        },
                    },
                ],
            },
        };
    }
    return {
        useProps(context, item, props): Omit<BlockItemViewProps, 'children'> {
            // State for menu visibility based on typing, managed locally by each BlockItem instance
            const [localIsTyping, setLocalIsTyping] = useState(false);
            // ADDED: State to track if mouse has moved over the item after typing stopped, allowing menu to show.
            const [allowMenuVisibilityOnHover, setAllowMenuVisibilityOnHover] =
                useState(true); // Initial true: menu can show on hover before any typing

            // Debounced function to set typing to false after a period of inactivity
            const debouncedMarkAsNotTyping = useDebouncedCallback(() => {
                setLocalIsTyping(false);
                // IMPORTANT: We don't set allowMenuVisibilityOnHover to true here.
                // It must wait for a mouse move for the menu to become visible on hover again.
            }, 750); // Adjust timeout as needed (e.g., 750ms)

            useEffect(() => {
                const handleGlobalKeyDown = (event: KeyboardEvent) => {
                    // Basic check for actual typing keys - can be refined
                    if (
                        event.key.length === 1 ||
                        event.key === 'Backspace' ||
                        event.key === 'Enter' ||
                        event.key === 'Delete'
                    ) {
                        if (!event.ctrlKey && !event.metaKey && !event.altKey) {
                            // Ignore shortcuts
                            setLocalIsTyping(true);
                            setAllowMenuVisibilityOnHover(false); // Hide menu and require mouse move to reshow on hover
                            debouncedMarkAsNotTyping();
                        }
                    }
                };

                // Add listener to the document in the capture phase
                document.addEventListener('keydown', handleGlobalKeyDown, true);

                // Cleanup function to remove listener and cancel debounce on unmount
                return () => {
                    document.removeEventListener('keydown', handleGlobalKeyDown, true);
                    debouncedMarkAsNotTyping.cancel();
                };
            }, [debouncedMarkAsNotTyping]); // Dependency array includes the debounced function

            const handleMouseMoveOnGroup = () => {
                if (localIsTyping) {
                    // If was typing (or debounce is pending for localIsTyping)
                    setLocalIsTyping(false); // Immediately mark as not actively typing
                    debouncedMarkAsNotTyping.cancel(); // Cancel any pending debounce for marking as not typing
                }
                if (!allowMenuVisibilityOnHover) {
                    // If menu was hidden and waiting for a mouse move
                    setAllowMenuVisibilityOnHover(true); // Allow it to show on hover now
                }
            };

            return {
                onMouseMove: handleMouseMoveOnGroup,
                insert: getInsertMenu(item, props),
                menu: {
                    isVisible: !localIsTyping && allowMenuVisibilityOnHover,
                    popover: {
                        isOpen: false,
                        onOpen: () => {},
                        onClose: () => {},
                    },
                    items: [
                        {
                            kind: 'item',
                            id: 'suggest-change',
                            description: 'Suggest change',
                            Icon: BiCommentEdit,
                            colorScheme: null,
                            button: {
                                onClick() {
                                    console.log('Bold');
                                },
                            },
                        },
                        {
                            kind: 'item',
                            id: 'add-comment',
                            description: 'Add a comment',
                            Icon: BiCommentAdd,
                            colorScheme: null,
                            button: {
                                onClick() {
                                    console.log('Bold');
                                },
                            },
                        },
                        {
                            kind: 'item',
                            id: 'delete',
                            description: 'Delete',
                            colorScheme: 'red',
                            Icon: HiOutlineTrash,
                            button: {
                                onClick() {
                                    props.adapter.delete();
                                },
                            },
                        },
                    ],
                },
            };
        },
    };
}
